// Die Miniaturwelt: Insel, eigenes Werk, Stationen, Straßen, Fahrzeuge, Wolken und Beschriftungen.
import * as THREE from "three"
import { CSS2DObject } from "three/addons/renderers/CSS2DRenderer.js"
import gsap from "gsap"
import { COLORS } from "./palette.js"
import { buildFactory } from "./factories.js"
import { buildStation, STATION_LAYOUT, STATION_ENTRANCES } from "./stations.js"
import { at, box, cylinder, tree, roundTree, truck, car, plane, pylon } from "./builders.js"
import { Co2Cloud } from "../fx/clouds.js"
import { Mover } from "../fx/movers.js"
import { rng } from "../game/rng.js"
import { formatTonnes } from "../ui/format.js"
import { mergeStatic } from "./merge.js"
import { People, peopleFor } from "./people.js"
import { buildTerrain, buildForest } from "./terrain.js"
import { buildEasterEggs, eggFor } from "./eggs.js"
import { buildGag, gagTargetFor } from "./gags.js"
import { buildAttraction } from "./attractions.js"
import { populateStation } from "./stationLife.js"
import { clearBubbles } from "./bubble.js"
import { railKeepOut, distanceToRail } from "./rail.js"

const ISLAND_RADIUS = 66
// Ringstraße um den Werkshof (Mittellinie); Zufahrten enden außen am Ring.
const RING = { hx: 11.6, hz: 9.1, width: 1.8 }

// Punkt auf der Ringstraße in Richtung (x, z).
function ringPoint(x, z) {
  const t = Math.min(RING.hx / Math.abs(x || 1e-6), RING.hz / Math.abs(z || 1e-6))
  return new THREE.Vector3(x * t, 0, z * t)
}
const QUALITY_CLASSES = { A: "bg-green text-white", B: "bg-lime text-navy", C: "bg-warn text-navy", D: "bg-bad text-white" }

export class World {
  constructor(scene, smoke, data) {
    this.scene = scene
    this.smoke = smoke
    this.data = data
    this.root = new THREE.Group()
    scene.add(this.root)
    this.buildIsland()
    this.dynamic = new THREE.Group()
    this.root.add(this.dynamic)
    this.stations = {}
    this.movers = []
    this.extras = []
    this.time = 0
  }

  // Landschaft, Wald und Fundstücke sind statisch und bleiben über alle Branchen erhalten.
  buildIsland() {
    this.eggs = buildEasterEggs()
    this.root.add(buildTerrain(), buildForest({ keepOut: [...this.eggs.keepOut, ...railKeepOut()] }), this.eggs.group)
    for (const egg of this.eggs.eggs) {
      if (egg.userData.smokeAt) this.smoke.addEmitter({ position: egg.userData.smokeAt, rate: 1.2, size: 0.6, color: COLORS.smokeClean, rise: 1.1 })
    }
  }

  focusStation(id) {
    for (const station of Object.values(this.stations)) station.cloud.setFaded(station.id === id)
  }

  stationLives() {
    return Object.values(this.stations).map((station) => station.life).filter(Boolean)
  }

  pickPerson(raycaster) {
    const figure = this.people?.pick(raycaster)
    if (figure) return figure
    const crew = this.stationLives().flatMap((life) => life.figures.filter(() => life.figures[0]?.parent?.visible))
    const hit = raycaster.intersectObjects(crew, true)[0]
    let object = hit?.object
    while (object && !object.userData.stationPerson) object = object.parent
    return object ?? null
  }

  pokePerson(figure) {
    if (!figure.userData.stationPerson) return this.people?.poke(figure)
    this.stationLives().find((life) => life.figures.includes(figure))?.poke(figure)
  }

  // Fundstücke in der Landschaft plus eines je sichtbarer Station
  allEggs() {
    return [...this.eggs.eggs, ...this.stationLives().map((life) => life.egg).filter((egg) => egg?.parent?.visible)]
  }

  pickGag(raycaster) {
    const targets = [...(this.gag?.targets || []), ...(this.attraction?.targets || [])]
    if (!targets.length) return null
    const hit = raycaster.intersectObjects(targets, true)[0]
    return hit ? gagTargetFor(hit.object) : null
  }

  clickGag(target) {
    target.userData.gag.click(target)
  }

  pickEgg(raycaster) {
    const hit = raycaster.intersectObjects(this.allEggs(), true)[0]
    return hit ? eggFor(hit.object) : null
  }

  get factory() {
    return this.stations.factory
  }

  // Baut Werk und alle Stationen mit Emissionen > 0 für diese Branche neu auf.
  setIndustry(industry, employees, baselineStations) {
    this.clear()
    this.moversVisible = false
    this.industry = industry
    this.maxTonnes = Math.max(...Object.values(baselineStations))
    const visibleIds = Object.keys(STATION_LAYOUT).filter((id) => (baselineStations[id] || 0) > 0)

    const factoryGroup = buildFactory(industry)
    this.dynamic.add(factoryGroup)
    this.registerStation("factory", factoryGroup, { cloudHeight: 13, color: COLORS.cloudScope12 })
    // Gag-Requisiten zuerst, damit die Figuren ihnen Platz lassen
    this.gag = buildGag(industry, factoryGroup)
    this.attraction = buildAttraction(industry, factoryGroup, this.gag)
    this.people = new People(factoryGroup, industry)
    this.gag?.attach(this.people)
    this.people.setCount(peopleFor(employees), { animate: false })

    for (const id of visibleIds) {
      const g = buildStation(id)
      g.visible = false
      this.dynamic.add(g)
      this.registerStation(id, g, { cloudHeight: id === "suburb" ? 8 : id === "airport" ? 14 : 12 })
    }

    this.connections = new THREE.Group()
    this.connections.visible = this.connectionsVisible ?? true
    this.dynamic.add(this.connections)
    this.buildRoads(visibleIds)
    this.populateStations(visibleIds)
    this.buildPowerLine()
    this.buildMovers(visibleIds)
    this.scatterTrees(visibleIds)
  }

  registerStation(id, group, { cloudHeight, color = COLORS.cloudScope3 }) {
    const cloud = new Co2Cloud({ color, height: cloudHeight })
    group.add(cloud.group)
    const label = this.createLabel(id)
    label.position.set(0, cloudHeight + 4.5, 0)
    group.add(label)
    const station = { id, group, cloud, label, tonnes: 0, emitters: [] }
    for (const local of group.userData.smoke || []) {
      const position = local.clone().add(group.position)
      station.emitters.push(this.smoke.addEmitter({ position, rate: 0, size: id === "factory" ? 1.1 : 0.9 }))
    }
    this.stations[id] = station
  }

  createLabel(id) {
    const config = this.data.stations.stations[id]
    // Das äußere Element positioniert der CSS2D-Renderer per transform. Skalierung und
    // Übergänge sitzen deshalb auf dem inneren Element, sonst verrutscht das Label beim Hover.
    const element = document.createElement("button")
    element.type = "button"
    element.dataset.station = id
    element.className = "station-label group pointer-events-auto"
    element.innerHTML = `
      <span data-role="card" class="flex -translate-y-1/2 flex-col items-center rounded-2xl bg-navy/85 px-3 py-1.5 text-center text-white shadow-lg ring-1 ring-white/15 backdrop-blur transition-[scale,background-color,box-shadow] group-hover:scale-105 group-hover:bg-navy">
        <span class="text-[16px] font-semibold leading-tight whitespace-nowrap">${config.label}</span>
        <span class="flex items-center gap-1.5 text-[14px] whitespace-nowrap text-white/75">
          <span data-role="tonnes"></span>
          <span data-role="quality" class="hidden rounded px-1 text-[11px] font-bold"></span>
        </span>
      </span>`
    const object = new CSS2DObject(element)
    object.visible = false
    return object
  }

  buildRoads(visibleIds) {
    const material = new THREE.MeshStandardMaterial({ color: COLORS.road, roughness: 1 })
    const ringDashes = []
    const spokeDashes = []
    const addSegment = (from, to, { dashStart = 1.5, dashEnd = 1.5, parent = this.dynamic, dashes = ringDashes } = {}) => {
      const length = from.distanceTo(to)
      const angle = -Math.atan2(to.z - from.z, to.x - from.x)
      const road = new THREE.Mesh(new THREE.BoxGeometry(length, 0.06, RING.width), material)
      road.position.set((from.x + to.x) / 2, 0.03, (from.z + to.z) / 2)
      road.rotation.y = angle
      road.receiveShadow = true
      parent.add(road)
      for (let d = dashStart; d < length - dashEnd; d += 3) {
        const t = d / length
        const dash = at(box(1.1, 0.02, 0.14, COLORS.roadLine), from.x + (to.x - from.x) * t, 0.06, from.z + (to.z - from.z) * t)
        dash.rotation.y = angle
        dash.castShadow = false
        parent.add(dash)
        dashes.push(dash)
      }
    }

    // Ring um den Hof: zwei lange Seiten (inkl. Ecken) und zwei kurze Seiten dazwischen.
    const { hx, hz, width } = RING
    addSegment(new THREE.Vector3(-hx - width / 2, 0, -hz), new THREE.Vector3(hx + width / 2, 0, -hz))
    addSegment(new THREE.Vector3(-hx - width / 2, 0, hz), new THREE.Vector3(hx + width / 2, 0, hz))
    addSegment(new THREE.Vector3(-hx, 0, -hz + width / 2), new THREE.Vector3(-hx, 0, hz - width / 2))
    addSegment(new THREE.Vector3(hx, 0, -hz + width / 2), new THREE.Vector3(hx, 0, hz - width / 2))

    for (const id of visibleIds) {
      const [sx, sz] = STATION_LAYOUT[id]
      const [ex, ez] = STATION_ENTRANCES[id]
      const x = sx + ex
      const z = sz + ez
      const from = ringPoint(x, z)
      // Zufahrt beginnt auf der Mittellinie des Rings: ihre Endkanten liegen so immer
      // innerhalb der Ringfahrbahn, auch wenn sie schräg auf den Ring trifft.
      // Sie endet am Eingang der Station (meist die Mitte, beim Flughafen der Parkplatz).
      const to = new THREE.Vector3(x, 0, z)
      addSegment(from, to, { dashStart: 2.5, dashEnd: 4, parent: this.connections, dashes: spokeDashes })
      this.stations[id].road = { from, to, length: from.distanceTo(to) }
    }
    mergeStatic(ringDashes, this.dynamic)
    mergeStatic(spokeDashes, this.connections)
  }

  // Personen und Fundstücke an den Stationen: nicht auf Straßen oder der Bahnstrecke
  populateStations(visibleIds) {
    const roads = visibleIds.map((id) => this.stations[id].road).filter(Boolean)
    const onRoad = (x, z) => roads.some(({ from, to }) => {
      const dx = to.x - from.x
      const dz = to.z - from.z
      const t = THREE.MathUtils.clamp(((x - from.x) * dx + (z - from.z) * dz) / (dx * dx + dz * dz), 0, 1)
      return Math.hypot(x - from.x - dx * t, z - from.z - dz * t) < 2.2
    })
    for (const id of visibleIds) {
      this.stations[id].life = populateStation(id, this.stations[id].group, (x, z) => onRoad(x, z) || distanceToRail(x, z) < 2.6)
    }
  }

  buildPowerLine() {
    const plant = this.stations.power_plant
    if (!plant) return
    const [x, z] = STATION_LAYOUT.power_plant
    const points = []
    const count = 4
    for (let i = 1; i <= count; i++) {
      const t = i / (count + 1)
      const p = at(pylon(6), x * t + 3, 0, z * t)
      p.rotation.y = -Math.atan2(z, x) + Math.PI / 2
      this.connections.add(p)
      points.push(new THREE.Vector3(x * t + 3, 5.1, z * t))
    }
    const curve = new THREE.CatmullRomCurve3([new THREE.Vector3(x + 3, 4, z), ...points, new THREE.Vector3(4, 3.5, -4)])
    this.powerLine = new THREE.Mesh(new THREE.TubeGeometry(curve, 60, 0.08, 4), new THREE.MeshStandardMaterial({ color: COLORS.navy600, emissive: COLORS.green, emissiveIntensity: 0 }))
    this.connections.add(this.powerLine)
  }

  // Zufahrten und Stromleitung: während der Werksauswahl ausgeblendet, damit nur das Werk im Fokus steht.
  setConnectionsVisible(visible) {
    this.connectionsVisible = visible
    if (this.connections) this.connections.visible = visible
  }

  buildMovers(visibleIds) {
    const roadPoints = (id, side = 0.45) => {
      const { from, to } = this.stations[id].road
      const normal = new THREE.Vector3(-to.z, 0, to.x).normalize().multiplyScalar(side)
      return [from.clone().lerp(to, 0.8).add(normal), from.clone().add(normal)]
    }
    const suppliers = ["steel_mill", "chem_plant", "component_works", "farm", "quarry"].filter((id) => visibleIds.includes(id))
    suppliers.forEach((id, i) => this.addMover(truck(COLORS.wall, COLORS.red), roadPoints(id), { speed: 5, offset: (i * 0.37) % 1, tag: ["trucks", id] }))
    if (visibleIds.includes("logistics_hub")) {
      this.addMover(truck(COLORS.wall, COLORS.navy600), roadPoints("logistics_hub"), { speed: 5, tag: ["trucks", "logistics_hub"] })
    }
    if (visibleIds.includes("customers")) {
      this.addMover(truck(COLORS.wall, COLORS.green), roadPoints("customers", -0.45), { speed: 4.5, offset: 0.5, tag: ["trucks", "customers"] })
    }
    if (visibleIds.includes("suburb")) {
      const colors = [COLORS.red, COLORS.slate, COLORS.orange, COLORS.white]
      colors.forEach((color, i) => this.addMover(car(color), roadPoints("suburb", i % 2 ? 0.45 : -0.45), { speed: 6, offset: i * 0.25, tag: ["commuters", "suburb"] }))
    }
    if (visibleIds.includes("airport")) {
      const [x, z] = STATION_LAYOUT.airport
      const loop = []
      for (let i = 0; i <= 24; i++) {
        const a = (i / 24) * Math.PI * 2
        loop.push(new THREE.Vector3(x + Math.cos(a) * 14, 16 + Math.sin(a * 2) * 1.5, z + Math.sin(a) * 10))
      }
      const p = plane()
      this.addMover(p, loop, { speed: 9, pingPong: false, tag: ["plane", "airport"] })
    }
  }

  addMover(object, points, { tag = [], ...options }) {
    object.visible = this.moversVisible ?? false
    this.dynamic.add(object)
    const mover = new Mover(object, points, options)
    mover.tags = tag
    this.movers.push(mover)
    return mover
  }

  moversTagged(tag) {
    return this.movers.filter((m) => m.tags.includes(tag))
  }

  scatterTrees(visibleIds) {
    const random = rng(1234)
    const centers = [[0, 0], ...visibleIds.map((id) => STATION_LAYOUT[id])]
    const nearRoad = (px, pz) => visibleIds.some((id) => {
      const [x, z] = STATION_LAYOUT[id]
      const t = THREE.MathUtils.clamp((px * x + pz * z) / (x * x + z * z), 0, 1)
      return Math.hypot(px - x * t, pz - z * t) < 2.5
    })
    const trees = []
    for (let i = 0; i < 400 && trees.length < 60; i++) {
      const angle = random() * Math.PI * 2
      const radius = 14 + random() * (ISLAND_RADIUS - 18)
      const x = Math.cos(angle) * radius
      const z = Math.sin(angle) * radius
      const nearRing = Math.abs(x) < RING.hx + 3 && Math.abs(z) < RING.hz + 3
      if (nearRing || distanceToRail(x, z) < 3 || centers.some(([cx, cz]) => Math.hypot(x - cx, z - cz) < 12) || nearRoad(x, z)) continue
      const t = random() > 0.5 ? tree(0.8 + random() * 0.7) : roundTree(0.8 + random() * 0.6)
      this.dynamic.add(at(t, x, 0, z, random() * Math.PI))
      trees.push(t)
    }
    mergeStatic(trees, this.dynamic)
  }

  clear() {
    for (const station of Object.values(this.stations)) station.emitters.forEach((e) => this.smoke.removeEmitter(e))
    for (const extra of this.extras) extra.dispose?.()
    // HTML-Elemente (Sprechblasen, Labels) der alten Szene sofort entfernen; der
    // CSS2D-Renderer räumt sie nicht auf, wenn nur eine Elterngruppe entfernt wird.
    clearBubbles()
    this.dynamic.traverse((object) => {
      if (object.isCSS2DObject) object.element.remove()
    })
    this.dynamic.clear()
    this.stations = {}
    this.movers = []
    this.extras = []
    this.powerLine = null
    this.people = null
    this.gag = null
    this.attraction = null
  }

  // --- Darstellung ---------------------------------------------------------

  cloudScale(tonnes) {
    if (!tonnes || tonnes <= 0) return 0
    return 0.45 + 2.6 * Math.sqrt(tonnes / this.maxTonnes)
  }

  setStationTonnes(id, tonnes, { animate = true, delay = 0 } = {}) {
    const station = this.stations[id]
    if (!station) return
    const before = station.tonnes
    station.tonnes = tonnes
    if (station.group.visible || id === "factory") station.cloud.setSize(this.cloudScale(tonnes), { duration: animate ? 1.4 : 0.01, delay })
    const target = station.label.element.querySelector("[data-role=tonnes]")
    if (animate && before > 0) {
      const counter = { value: before }
      gsap.to(counter, { value: tonnes, duration: 1.4, delay, onUpdate: () => (target.textContent = formatTonnes(counter.value)) })
    } else {
      target.textContent = formatTonnes(tonnes)
    }
    // Rauchmenge folgt den Emissionen relativ zum Ausgangswert
    const base = station.baseTonnes ?? tonnes
    station.baseTonnes = base
    const ratio = base > 0 ? tonnes / base : 0
    station.emitters.forEach((emitter) => {
      emitter.rate = station.smokeOn ? 2.2 * ratio + 0.2 : 0
    })
  }

  setQuality(id, quality, uncertainty) {
    const station = this.stations[id]
    if (!station) return
    const badge = station.label.element.querySelector("[data-role=quality]")
    badge.className = `rounded px-1 text-[11px] font-bold ${QUALITY_CLASSES[quality]}`
    badge.textContent = `Datenqualität ${quality}`
    station.cloud.setUncertainty(uncertainty)
  }

  showQuality(visible) {
    for (const station of Object.values(this.stations)) {
      station.label.element.querySelector("[data-role=quality]").classList.toggle("hidden", !visible)
    }
  }

  setSmoke(id, on) {
    const station = this.stations[id]
    if (!station) return
    station.smokeOn = on
    this.setStationTonnes(id, station.tonnes, { animate: false })
  }

  showLabels(ids, visible = true) {
    for (const id of ids) if (this.stations[id]) this.stations[id].label.visible = visible
  }

  // Stationen erscheinen nacheinander (Rauszoom-Moment).
  setEmployees(employees) {
    this.people?.setCount(peopleFor(employees))
  }

  setMoversVisible(visible) {
    this.moversVisible = visible
    for (const mover of this.movers) mover.object.visible = visible
  }

  revealStations({ stagger = 0.18, delay = 0 } = {}) {
    // Zufahrten und Stromleitung erscheinen zusammen mit den ersten Stationen
    setTimeout(() => this.setConnectionsVisible(true), delay * 1000)
    setTimeout(() => this.setMoversVisible(true), (delay + 1) * 1000)
    const ids = this.stationIds().filter((id) => id !== "factory")
    ids.sort((a, b) => this.distance(a) - this.distance(b))
    ids.forEach((id, i) => {
      const station = this.stations[id]
      const d = delay + i * stagger
      station.group.visible = true
      station.group.scale.setScalar(0.001)
      gsap.to(station.group.scale, { x: 1, y: 1, z: 1, duration: 0.9, delay: d, ease: "back.out(1.7)" })
      station.cloud.setSize(this.cloudScale(station.tonnes), { delay: d + 0.5 })
      setTimeout(() => {
        station.smokeOn = true
        this.setSmoke(id, true)
        station.label.visible = true
      }, (d + 0.7) * 1000)
    })
    return delay + ids.length * stagger + 1.2
  }

  showAllStationsInstantly() {
    this.setMoversVisible(true)
    for (const id of this.stationIds()) {
      const station = this.stations[id]
      station.group.visible = true
      station.group.scale.setScalar(1)
      station.cloud.setSize(this.cloudScale(station.tonnes), { duration: 0.01 })
      this.setSmoke(id, true)
    }
  }

  stationIds() {
    return Object.keys(this.stations)
  }

  distance(id) {
    const [x, z] = STATION_LAYOUT[id] || [0, 0]
    return Math.hypot(x, z)
  }

  stationPosition(id) {
    if (id === "factory") return new THREE.Vector3(0, 0, 0)
    const [x, z] = STATION_LAYOUT[id]
    return new THREE.Vector3(x, 0, z)
  }

  highlight(ids) {
    const set = new Set(ids)
    for (const station of Object.values(this.stations)) {
      const card = station.label.element.querySelector("[data-role=card]")
      card.classList.toggle("ring-lime", set.has(station.id))
      card.classList.toggle("ring-2", set.has(station.id))
      card.classList.toggle("scale-110", set.has(station.id))
    }
  }

  update(dt, time) {
    this.time = time
    for (const station of Object.values(this.stations)) station.cloud.update(time)
    for (const mover of this.movers) mover.update(dt)
    this.people?.update(dt, time)
    this.gag?.update(dt, time)
    this.attraction?.update(dt, time)
    this.eggs.update(dt, time)
    for (const station of Object.values(this.stations)) {
      station.life?.update(dt, time)
      station.group.userData.animate?.(time)
    }
    for (const extra of this.extras) extra.update?.(dt, time)
  }
}
